import {
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  ViewEncapsulation
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import firebase from 'firebase/app';
import { firstValueFrom, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { SafeLoggerService } from '../../services/safe-logger.service';
import { FirebaseUtilitiesService } from '../../services/firebase-utilities.service';
import { FilesService } from '../../services/files.service';
import { FoldersService } from '../../services/folders.service';
import { UIMessagingService } from '../../services/uimessaging.service';
import { GapiOperationsService } from '../../services/gapi-operations.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { ClientProfileSharingService } from '../../services/client-profile-sharing.service';
import { ClientProfileLibraryFacade, LibraryContext } from '../../services/client-profile-library.facade';
import { ClientMatterService } from '../../services/client-matter.service';
import { AlgoliaService } from '../../services/algolia.service';
import { UploadHelperService } from '../../services/upload-helper.service';
import { PatientDocumentsMode } from '../../models/patient-documents-mode';
import { ShareFilesDialogResult } from '../../models/ShareFilesDialogResult';
import { UploadTypes } from '../../models/UploadTypes';
import { UserPlans } from '../../dictionaries/UserPlans';
import { UserRoles } from '../../dictionaries/UserRoles';
import { folderColors } from '../../dictionaries/folderColors';
import { ShareFilesComponent } from '../share-files/share-files.component';
import { ShareFilesSpokeComponent } from '../share-files-spoke/share-files-spoke.component';
import { UploadDialogComponent } from '../ui/uploaddialog/uploaddialog.component';
import { AllNotesDialogComponent } from '../ui/all-notes-dialog/all-notes-dialog.component';
import { NoteDialogComponent } from '../ui/note-dialog/note-dialog.component';
import { ViewAllNotesDialogData } from '../../models/ViewAllNotesDialogData';
import { NotificationTypes } from '../../models/NotificationTypes';
import { FileCommentsAttachmentsService } from '../../services/file-comments-attachments.service';
import { AccordionFolder } from '../../services/accordion-data.service';
import { FolderFile, HomeFolder } from '../home-folders-accordion/home-folders-accordion.component';

const DISCS_FOLDER_NAME = 'Discs';
const LIBRARY_FOLDER_NAME = 'Library';
const INDEX_FOLDER_NAME = 'all';
const NOT_PLAYABLE_VIDEO_EXTENSIONS = ['mpeg', 'vob'];

@Component({
  selector: 'app-patient-documents-flow',
  templateUrl: './patient-documents-flow.component.html',
  styleUrls: ['./patient-documents-flow.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class PatientDocumentsFlowComponent implements OnInit, OnDestroy {
  @Input() mode: PatientDocumentsMode | null = null;
  @Input() patientID: string | null = null;
  @Input() patientFromParent: any | null = null;

  // ── Entry / mode state ────────────────────────────────────────────────────
  resolvedMode: PatientDocumentsMode = 'share';
  isRouteMode: boolean = false;
  routePatientID: string | null = null;

  // ── Patient / case state ──────────────────────────────────────────────────
  patient: any = null;
  casename: string = '';
  patientName: string = '';
  defaultFolders: boolean = false;
  ownerID: string = '';

  // ── User / role state ─────────────────────────────────────────────────────
  userOwnerID: string = '';
  realRole: string = '';
  isSpoke: boolean = false;
  isHub: boolean = false;
  userIsConsultant: boolean = false;
  userIsClient: boolean = false;
  userFacility: string = '';

  // ── Share action data ─────────────────────────────────────────────────────
  facilities: any[] = [];
  activeConsultants: any[] = [];
  availableConsultants: any[] = [];
  activeFacilitiesUsers: any[] = [];
  availableFacilitiesUsers: any[] = [];
  private consultantsLoaded = false;

  // ── Upload flow state ─────────────────────────────────────────────────────
  private uploadSubscription: Subscription | null = null;
  private infectedFilesSubscription: Subscription | null = null;

  // ── Infected files ────────────────────────────────────────────────────────
  infectedFiles: any[] = [];

  // ── Search state ──────────────────────────────────────────────────────────
  isSearchActive: boolean = false;
  searchResults: any[] = [];

  // ── In-progress disc notifications ───────────────────────────────────────
  private discNotificationIds: string[] = [];

  // ── Workspace bootstrap state ─────────────────────────────────────────────
  isLoadingPatient: boolean = false;
  initError: string | null = null;
  workspaceReady: boolean = false;
  isRefreshing: boolean = false;
  foldersStructure: any;

  // ── Folder / file workspace state ─────────────────────────────────────────
  caseFolders: any[] = [];
  currentFolder: string = '';
  currentFolderName: string = '';
  files: any[] = [];
  allFiles: any[] = [];
  accordionFolders: AccordionFolder[] = [];
  filesLoaded: { [folderId: string]: FolderFile[] } = {};
  readonly discsFolderName: string = DISCS_FOLDER_NAME;
  readonly UserRoles = UserRoles;

  get pageTitle(): string {
    return this.resolvedMode === 'retrieve' ? 'Retrieve Documents' : 'Share Documents';
  }

  get primaryActionLabel(): string {
    return this.resolvedMode === 'retrieve' ? 'Retrieve' : 'Share';
  }

  get accordionPermissions(): any {
    const canEdit = [UserRoles.owner, UserRoles.admin, UserRoles.associate].includes(this.realRole as any);
    return { createfolder: canEdit, deletefiles: canEdit, editfiles: canEdit, sharefileicon: true };
  }

  get permissions_uploadfiles(): boolean {
    return [UserRoles.owner, UserRoles.admin, UserRoles.associate].includes(this.realRole as any);
  }

  get permissions_createfolder(): boolean {
    return [UserRoles.owner, UserRoles.admin, UserRoles.associate].includes(this.realRole as any);
  }

  readonly addClientUploadCondition: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth_$: AuthService,
    private safeLogger: SafeLoggerService,
    private firebase_utilities_$: FirebaseUtilitiesService,
    private files_$: FilesService,
    private folders_$: FoldersService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private uiMessaging_$: UIMessagingService,
    private clientProfileSharing_$: ClientProfileSharingService,
    private uploadHelper_$: UploadHelperService,
    private fileCommentsAttachments_$: FileCommentsAttachmentsService,
    private gapiOperations_$: GapiOperationsService,
    private sessionStorage_$: SessionStorageService,
    private libraryFacade_$: ClientProfileLibraryFacade,
    private clientMatter_$: ClientMatterService,
    private algolia_$: AlgoliaService,
    @Optional() public dialogRef: MatDialogRef<PatientDocumentsFlowComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: any
  ) {
    this.foldersStructure = {
      name: 'Home',
      id: INDEX_FOLDER_NAME,
      children: [
        { name: LIBRARY_FOLDER_NAME, id: LIBRARY_FOLDER_NAME, children: [] },
        { name: DISCS_FOLDER_NAME, id: DISCS_FOLDER_NAME, children: [] }
      ]
    };
  }

  async ngOnInit(): Promise<void> {
    this.resolveMode();
    this.resolveEntryContext();
    await this.loadPatientIfNeeded();
    await this.initializeWorkspace();
  }

  // ── Mode resolution ───────────────────────────────────────────────────────

  /** Resolve mode: @Input → dialog data → route data → URL segment → query param → 'share'. */
  resolveMode(): void {
    if (this.mode) {
      this.resolvedMode = this.mode;
      return;
    }
    const dialogMode = this.dialogData?.mode as PatientDocumentsMode | undefined;
    if (dialogMode === 'share' || dialogMode === 'retrieve') {
      this.resolvedMode = dialogMode;
      return;
    }
    const routeDataMode = this.route?.snapshot?.data?.['mode'] as PatientDocumentsMode | undefined;
    if (routeDataMode === 'share' || routeDataMode === 'retrieve') {
      this.resolvedMode = routeDataMode;
      return;
    }
    const urlSegments = this.route?.snapshot?.url ?? [];
    if (urlSegments.map(s => s.path).join('/').includes('retrieve')) {
      this.resolvedMode = 'retrieve';
      return;
    }
    const queryMode = this.route?.snapshot?.queryParams?.['mode'] as PatientDocumentsMode | undefined;
    if (queryMode === 'share' || queryMode === 'retrieve') {
      this.resolvedMode = queryMode;
      return;
    }
    this.resolvedMode = 'share';
  }

  // ── Entry context ─────────────────────────────────────────────────────────

  /** Resolve IDs and any already-available patient object from @Input or dialog data. */
  resolveEntryContext(): void {
    const routeParam = this.route?.snapshot?.paramMap?.get('patientID');
    this.isRouteMode = !!routeParam;
    this.routePatientID = routeParam ?? this.patientID ?? null;

    const earlyPatient = this.patientFromParent ?? this.dialogData?.patient ?? null;
    if (earlyPatient) {
      this.patient = earlyPatient;
      this.extractPatientFields(earlyPatient);
    }
  }

  // ── Patient loading ───────────────────────────────────────────────────────

  /** Load patient from Firestore when only an ID is available (route or @Input patientID). */
  async loadPatientIfNeeded(): Promise<void> {
    if (this.patient) {
      this.auth_$.setPatientProfile(this.patient);
      return;
    }

    const idToLoad = this.routePatientID;
    if (!idToLoad) {
      return;
    }

    this.isLoadingPatient = true;
    this.auth_$.showLoader('Loading patient data...');

    try {
      const doc = await firebase.firestore().collection('patients').doc(idToLoad).get();
      if (!doc.exists) {
        this.initError = 'not-found';
        this.safeLogger.error('Patient document not found');
        this.router.navigate(['/']);
        return;
      }
      const patientData = doc.data();
      patientData.id = doc.id;
      this.patient = patientData;
      this.auth_$.setPatientProfile(this.patient);
      this.extractPatientFields(this.patient);
    } catch (error: any) {
      this.initError = 'load-error';
      this.safeLogger.error('Error loading patient data', error?.code ?? 'unknown');
      this.router.navigate(['/']);
    } finally {
      this.isLoadingPatient = false;
      this.auth_$.hideLoader();
    }
  }

  // ── Workspace initialization ──────────────────────────────────────────────

  /** Resolve user data, role, and plan after patient is loaded. */
  async initializeWorkspace(): Promise<void> {
    if (this.initError || !this.patient) {
      return;
    }

    this.ownerID = this.patient?.ownerID ?? '';

    await this.waitForUserData();

    const userData = this.auth_$.userData.getValue();
    this.realRole = userData['role'] ?? '';
    this.isSpoke = userData['plancode'] === UserPlans.spoke;
    this.isHub = !this.isSpoke;
    this.userIsConsultant = this.realRole === UserRoles.consultant;
    this.userIsClient = this.realRole === UserRoles.client;
    this.userFacility = userData['facility'] ?? '';

    this.applyModeSpecificDefaults();
    await this.loadInitialWorkspaceData();
    this.workspaceReady = true;
    this.subscribeToFileUploads();
    this.subscribeToInfectedFiles();
  }

  ngOnDestroy(): void {
    this.uploadSubscription?.unsubscribe();
    this.infectedFilesSubscription?.unsubscribe();
  }

  // ── Initial workspace loading ─────────────────────────────────────────────

  async loadInitialWorkspaceData(): Promise<void> {
    await this.loadFolders();
    this.selectInitialFolder();
    await this.loadInitialFiles();
  }

  async loadFolders(): Promise<void> {
    if (!this.casename) return;
    await this.checkAndCreateDefaultFolders();
    this.caseFolders = await this.firebase_utilities_$.getFolders(this.casename, {
      patientType: this.patient?.type,
      isSpoke: false,
      restrictedTo: null,
      ownerID: this.ownerID,
      userOwnerID: this.userOwnerID
    });
    this.foldersStructure = {
      name: 'Home',
      id: '',
      children: [
        { name: 'INDEX', id: INDEX_FOLDER_NAME, children: [] },
        ...this.caseFolders.map(folder => ({
          name: folder.name,
          id: folder.folderId,
          color: folder.color,
          children: []
        }))
      ]
    };
    this.accordionFolders = [
      { id: INDEX_FOLDER_NAME, name: 'INDEX' },
      ...this.caseFolders.map(folder => ({
        id: folder.folderId,
        name: folder.name,
        color: folder.color
      }))
    ];
  }

  selectInitialFolder(): void {
    if (!this.currentFolder) {
      this.currentFolder = INDEX_FOLDER_NAME;
      this.currentFolderName = 'INDEX';
    }
  }

  async loadInitialFiles(): Promise<void> {
    if (!this.casename) return;
    await this.loadFilesForFolder(this.currentFolder || INDEX_FOLDER_NAME);
    this.allFiles = [...this.files];
  }

  // ── Read-side folder / file navigation ───────────────────────────────────

  /** Navigate to a folder: update selection state and reload visible files. */
  async browseFolder(folderId: string, folderName: string): Promise<void> {
    this.currentFolder = folderId;
    this.currentFolderName = folderName;
    await this.loadFilesForFolder(folderId);
  }

  /** Reload files for the currently selected folder and sync filesLoaded map. */
  refreshCurrentFolderFiles(): void {
    const folderId = this.currentFolder || INDEX_FOLDER_NAME;
    this.loadFilesForFolder(folderId).then(() => {
      this.filesLoaded = { ...this.filesLoaded, [folderId]: this.files };
    });
  }

  /** Resolve a folder ID to its display name from the loaded case folders. */
  getFolderName(folderId: string): string {
    if (!folderId || folderId === INDEX_FOLDER_NAME) return 'INDEX';
    return this.caseFolders.find(f => f.folderId === folderId)?.name ?? '';
  }

  /** Resolve a folder ID to its full folder object from the loaded case folders. */
  getFolderById(folderId: string): any | null {
    if (!folderId) return null;
    return this.caseFolders.find(f => f.folderId === folderId) ?? null;
  }

  // ── Mode-specific defaults ────────────────────────────────────────────────

  applyModeSpecificDefaults(): void {
    if (this.resolvedMode === 'share') {
      this.openShareFlow();
    } else {
      this.openRetrieveFlow();
    }
  }

  openShareFlow(): void {
    this.ensureActionDataLoaded();
  }

  openRetrieveFlow(): void {
    this.ensureActionDataLoaded();
  }

  // ── Pre-action data preparation ───────────────────────────────────────────

  /** Kick off facilities + consultant + facility-user lists in parallel. Fire-and-forget; results land before dialog opens. */
  ensureActionDataLoaded(): void {
    this.loadFacilitiesForAction().then(() => this.loadConsultantsForAction());
    this.loadFacilityUsersForAction();
  }

  private async loadFacilitiesForAction(): Promise<void> {
    try {
      this.facilities = await this.firebase_utilities_$.getFacilities();
    } catch {
      this.facilities = [];
    }
  }

  private async loadConsultantsForAction(): Promise<void> {
    if (!this.casename || !this.ownerID) return;
    try {
      // Active must resolve first so it can be used as the exclusion list for available.
      const active = await this.firebase_utilities_$.getActiveFaciltiesConsultantsAndClientByClientId(
        [UserRoles.consultant, UserRoles.client],
        this.casename,
        this.ownerID,
        this.allFiles
      );
      this.activeConsultants = active ?? [];

      const available = await this.firebase_utilities_$.getAvailableAdmins(
        this.casename,
        UserRoles.consultant,
        this.activeConsultants,
        true,
        this.ownerID
      );
      this.availableConsultants = available ?? [];
      this.consultantsLoaded = true;
    } catch {
      this.activeConsultants = [];
      this.availableConsultants = [];
    }
  }

  private async loadFacilityUsersForAction(): Promise<void> {
    if (!this.casename || !this.ownerID) return;
    try {
      if (!this.isSpoke) {
        this.activeFacilitiesUsers = await this.firebase_utilities_$.getActiveFaciltiesAdminsByClientIdV2(
          this.casename, this.ownerID, null, true
        );
      } else {
        this.activeFacilitiesUsers = await this.firebase_utilities_$.getSpokesFacilitators();
      }

      this.availableFacilitiesUsers = await this.firebase_utilities_$.getAvailableAdmins(
        this.casename, UserRoles.admin, this.activeFacilitiesUsers, true, this.ownerID
      );

      if (this.userFacility) {
        const snap = await firebase.firestore().collection('users')
          .where('facility', '==', this.userFacility)
          .get();
        const allowedRoles = new Set([UserRoles.consultant, UserRoles.admin]);
        const existing = new Set([
          ...this.activeFacilitiesUsers.map((u: any) => u.email),
          ...this.availableFacilitiesUsers.map((u: any) => u.email)
        ]);
        const extra = snap.docs
          .map(d => ({ ...d.data(), id: d.id }))
          .filter((u: any) => allowedRoles.has(u.role) && !u.disabled && !existing.has(u.email));
        this.availableFacilitiesUsers = [...this.availableFacilitiesUsers, ...extra];
      }
    } catch {
      this.activeFacilitiesUsers = [];
      this.availableFacilitiesUsers = [];
    }
  }

  // ── Primary action ────────────────────────────────────────────────────────

  handlePrimaryAction(): void {
    this.openShareFilesDialog();
  }

  // ── Share / retrieve document action entry ────────────────────────────────

  async openShareFilesDialog(restoreState?: any): Promise<void> {
    if (!this.allFiles || this.allFiles.length === 0) {
      await this.loadInitialFiles();
    }

    if (!this.allFiles || this.allFiles.length === 0) {
      this.uiMessaging_$.toastMessage(
        'No files have been uploaded yet. Please upload at least one file before sharing.',
        'WARNING',
        { duration: 5000 }
      );
      return;
    }

    // Match old behavior: consultants are loaded lazily (once), facility users are always refreshed.
    if (!this.consultantsLoaded) {
      await this.loadConsultantsForAction();
    }
    await this.loadFacilityUsersForAction();

    const indexFolder = { folderId: INDEX_FOLDER_NAME, name: 'INDEX', count: this.allFiles.length };
    const dialogData: any = {
      patient: this.patient,
      allFiles: this.allFiles,
      files: this.files,
      caseFolders: [indexFolder, ...(this.caseFolders ?? [])],
      facilities: this.isSpoke
        ? this.facilities.filter(f => (this.auth_$.userData.getValue()['hubs'] ?? []).includes(f.id))
        : this.facilities,
      users: {
        activeConsultants: this.activeConsultants,
        activeFacilitiesUsers: this.activeFacilitiesUsers,
        availableFacilitiesUsers: this.availableFacilitiesUsers,
        availableConsultants: this.availableConsultants
      }
    };

    if (restoreState) {
      dialogData.restoreState = restoreState;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '1000px';
    dialogConfig.maxWidth = '95vw';
    dialogConfig.maxHeight = '90vh';
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = dialogData;

    const component = this.isSpoke ? ShareFilesSpokeComponent : ShareFilesComponent;
    const result: ShareFilesDialogResult = await firstValueFrom(
      this.dialog.open(component as any, dialogConfig).afterClosed()
    );
    await this.handleShareResult(result);
  }

  async handleShareResult(shareObject: ShareFilesDialogResult): Promise<void> {
    if (!shareObject) return;

    if ((shareObject as any).action === 'refreshAndReopen') {
      await this.loadConsultantsForAction();
      await new Promise<void>(resolve => setTimeout(resolve, 500));
      this.openShareFilesDialog((shareObject as any).currentState);
      return;
    }

    const userEmail = this.auth_$.userData.getValue()?.['email'] ?? '';

    try {
      if (this.clientProfileSharing_$.shouldShareWithFacilityFacilitators(shareObject)) {
        await this.clientProfileSharing_$.handleShareWithFacilityFacilitators(
          shareObject, this.userOwnerID, userEmail, this.userFacility
        );
      } else {
        await this.clientProfileSharing_$.handleShareWithConsultant(
          shareObject, this.casename, userEmail
        );
      }
    } catch (err: any) {
      this.safeLogger.error('Share action failed', err?.code ?? 'unknown');
      return;
    }

    if (shareObject.user?.email && shareObject.files?.length) {
      this.clientProfileSharing_$.sendNotification(
        shareObject.user.email,
        (shareObject as any).message ?? null,
        shareObject.files,
        this.casename
      );
    }

    const facilityName = shareObject.facility?.name ?? 'Unknown Facility';
    const recipientName = this.clientProfileSharing_$.shouldShareWithFacilityFacilitators(shareObject)
      ? `${facilityName} facilitators`
      : shareObject.user?.name ?? 'Unknown Consultant';
    const fileCount = shareObject.files?.length ?? 0;

    this.snackBar.open(`Successfully shared ${fileCount} file(s) with ${recipientName}`, 'Close', { duration: 3000 });
    this.handlePostActionRefresh();
  }

  handlePostActionRefresh(): void {
    this.loadFolders().then(() => this.refreshCurrentFolderFiles());
  }

  // ── Upload flow ───────────────────────────────────────────────────────────

  async openUploadDialog(): Promise<void> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '700px';
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.maxHeight = '70vw';
    dialogConfig.data = this.buildUploadDialogData();

    const dialogRef = this.dialog.open(UploadDialogComponent, dialogConfig);

    // Immediate in-dialog refresh (e.g. folder created during upload flow)
    const finishSub = dialogRef.componentInstance.finish.subscribe(async (data: any) => {
      if (data?.action === 'getfiles') {
        await this.handlePostUploadRefresh();
      }
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    finishSub.unsubscribe();
    await this.handleUploadResult(result);
  }

  private buildUploadDialogData(): any {
    return {
      id: 1,
      section: null,
      clioMatterId: null,
      title: 'Upload files and discs',
      currentFolder: { folderId: this.currentFolder, folderName: this.currentFolderName },
      uploadTypes: [
        { id: '1', name: UploadTypes.FILE },
        { id: '2', name: UploadTypes.VIDEO },
        { id: '3', name: UploadTypes.DICOMDisk },
        { id: '4', name: UploadTypes.VIDEODisk }
      ],
      ftime: null,
      fdate: '',
      basicfile: this.files,
      description: '',
      type: '',
      clientName: this.patientName,
      action_params: {
        files: '',
        type: '',
        desc: '',
        caseName: this.casename,
        creator: this.auth_$.userData.getValue()?.['uid'] ?? ''
      },
      ownerPlanCode: this.patient?.ownerPlanCode ?? null,
      ownerID: this.ownerID,
      userOwnerID: this.userOwnerID
    };
  }

  async handleUploadResult(result: any): Promise<void> {
    if (result !== 'refresh') return;
    await this.handlePostUploadRefresh();
  }

  async handlePostUploadRefresh(): Promise<void> {
    await this.loadFolders();
    this.refreshCurrentFolderFiles();
    await this.refreshAllFiles();
  }

  /** Subscribe to the upload service's fileUploaded signal and refresh on each completed upload. */
  subscribeToFileUploads(): void {
    const allowedRoles = new Set([UserRoles.owner, UserRoles.admin, UserRoles.associate]);
    if (this.realRole && !allowedRoles.has(this.realRole)) return;

    this.uploadSubscription?.unsubscribe();
    this.uploadSubscription = this.uploadHelper_$.fileUploaded.subscribe({
      next: async (uploaded: boolean) => {
        if (uploaded) {
          await this.handlePostUploadRefresh();
        }
      },
      error: () => { }
    });
  }

  // ── Notes / comments / attachments flow ──────────────────────────────────

  async openAddNoteDialog(file: any): Promise<void> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '400px';
    dialogConfig.data = this.buildNoteDialogData(file);

    const result = await firstValueFrom(
      this.dialog.open(NoteDialogComponent, dialogConfig).afterClosed()
    );
    if (result?.type === 'notesave') {
      await this.handleNoteResult(result);
    }
  }

  private buildNoteDialogData(file: any): any {
    const userData = this.auth_$.userData.getValue();
    return {
      title: 'Add Comments and/or Attachments',
      selected_file: file,
      useremail: userData?.['email'] ?? '',
      userRole: this.realRole,
      name: '',
      description: '',
      visibility: { consultants: true, clients: true }
    };
  }

  private async handleNoteResult(result: any): Promise<void> {
    const userData = this.auth_$.userData.getValue();
    const { docId, id, type, parentFolder, noteId } =
      await this.fileCommentsAttachments_$.addCommentsAndAttachments(
        result.selected_file,
        result.data,
        this.patientName,
        userData?.['userName'] ?? ''
      );
    await this.afterNoteAddRefresh({ fileDocId: docId, id, type, parentFolder, noteId });
  }

  private async afterNoteAddRefresh({
    id, type, parentFolder, noteId
  }: { fileDocId?: string; id: any; type: any; parentFolder: any; noteId: any }): Promise<void> {
    this.firebase_utilities_$.createNotifications(
      { id },
      { type: type === 'file' ? 1 : 2, uploadBlock: { casename: this.casename }, noteId },
      { parentFolderId: parentFolder },
      NotificationTypes.newnote
    );
    this.refreshCurrentFolderFiles();
  }

  async openCommentsAndAttachments(file: any): Promise<void> {
    let width = '85vw', height = '80vh', maxWidth = '85vw', maxHeight = '95vh';
    if (file.ftype === UploadTypes.DICOMDisk) {
      width = maxWidth = '95vw';
      height = maxHeight = '95vh';
    }

    const dialogData = this.buildAllNotesDialogData(file);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = width;
    dialogConfig.height = height;
    dialogConfig.maxWidth = maxWidth;
    dialogConfig.maxHeight = maxHeight;
    dialogConfig.autoFocus = true;
    dialogConfig.disableClose = false;
    dialogConfig.data = dialogData;

    const dialogRef = this.dialog.open(AllNotesDialogComponent, dialogConfig);
    firstValueFrom(dialogRef.afterClosed())
      .then(() => this.handleCommentsDialogClose(dialogData.fileid));

    const { ftype, fileName, viewerurl, fileId, filePath } = file;
    this.getFileViewData(ftype, fileName, viewerurl, fileId, filePath)
      .then(viewData => { if (dialogRef.componentInstance) dialogRef.componentInstance.setFileViewData(viewData); })
      .catch(() => { if (dialogRef.componentInstance) dialogRef.componentInstance.setFileViewData(null); });
  }

  private buildAllNotesDialogData(file: any): ViewAllNotesDialogData {
    const userData = this.auth_$.userData.getValue();
    const uid: string = userData?.['uid'] ?? '';
    const role: string = userData?.['role'] ?? '';
    const email: string = userData?.['email'] ?? '';
    const notifications: any[] = userData?.['notifications'] ?? [];
    const { notes, fileId, id, parentFolder, type } = file;

    return {
      title: 'View Comments & Attachments',
      message: `Filename: ${file.fileName ?? ''}`,
      selected_files: [file],
      notes: notes ?? [],
      fileViewData: null,
      fileid: fileId,
      fileDocId: id,
      patientName: this.casename,
      createdBy: email,
      currentFolder: this.currentFolder,
      currentFolderName: this.currentFolderName,
      parentFolder: parentFolder ?? '',
      userRole: role,
      userEmail: email,
      fileType: type ?? '',
      notifications: this.getNotificationsForFile(notifications, fileId, uid),
      restrictedProfiles: this.patient?.restrictedProfiles ?? [],
      allowedProfiles: this.patient?.allowedProfiles ?? [],
      fileClicked: file
    };
  }

  private handleCommentsDialogClose(fileId: any): void {
    this.auth_$.getNotifications(fileId).catch(() => {});
    this.refreshCurrentFolderFiles();
  }

  handlePostMutationRefresh(): void {
    this.loadFolders().then(() => this.refreshCurrentFolderFiles());
  }

  private getNotificationsForFile(notifications: any[], fileId: string, uid: string): any[] {
    return (notifications ?? [])
      .filter((n: any) => n.fileId === fileId)
      .filter((n: any) => n.userId === uid)
      .filter((n: any) => [NotificationTypes.modifiednote, NotificationTypes.newnote].includes(n.type));
  }

  // ── Accordion / row-action wiring ────────────────────────────────────────

  async handleAccordionFolderClick(folder: HomeFolder): Promise<void> {
    await this.browseFolder(folder.id, folder.name);
    this.filesLoaded = { ...this.filesLoaded, [folder.id]: this.files };
  }

  async handleAccordionLoadFolderContents(folderId: string): Promise<void> {
    await this.loadFilesForFolder(folderId);
    this.filesLoaded = { ...this.filesLoaded, [folderId]: this.files };
  }

  async handleAccordionRefreshFolderContents(folderId: string): Promise<void> {
    await this.loadFilesForFolder(folderId);
    this.filesLoaded = { ...this.filesLoaded, [folderId]: this.files };
  }

  handleAccordionFileClick(file: FolderFile): void {
    this.clearFileNotifications(file.fileId);
    this.openCommentsAndAttachments(file);
  }

  handleAccordionAddCommentOrAttachment(event: { file: FolderFile; event: Event }): void {
    this.openCommentsAndAttachments(event.file);
  }

  // ── File row actions ──────────────────────────────────────────────────────

  async handleAccordionDeleteFile(event: { file: FolderFile; event: Event }): Promise<void> {
    const { file } = event;
    if (!file.fileId) {
      this.uiMessaging_$.toastMessage('Cannot delete: missing file ID.', 'ERROR');
      return;
    }

    const { ConfirmationDialogComponent } = await import('../ui/confirmation-dialog/confirmation-dialog.component');
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: 'Confirm deletion',
      message: `Are you sure you want to delete this file?`,
      btnOkText: 'Yes',
      btnCancelText: 'No',
      deleteAllFiles: false,
      selected_files: [file]
    };

    const result = await firstValueFrom(
      this.dialog.open(ConfirmationDialogComponent, dialogConfig).afterClosed()
    );
    if (!result?.ok) return;

    try {
      if (file.id) {
        await this.files_$.deleteFileByDocId({ id: file.id, fileId: file.fileId });
      }
      if (file.filePath) {
        await this.files_$.deleteFilesFromStorage([file.filePath]);
      }
      this.clearFileNotifications(file.fileId);
      this.removeDeletedFileFromSearchResults(file.fileId);
      this.algolia_$.deleteManyFromAlgolia([file.fileId]).catch(() => {});
      this.uiMessaging_$.toastMessage('File deleted.', 'INFO');
      this.invalidateFilesLoadedAndRefresh();
    } catch (err: any) {
      this.safeLogger.error('Delete file failed', err?.code ?? 'unknown');
      this.uiMessaging_$.toastMessage('Error deleting file. Please try again.', 'ERROR');
    }
  }

  async handleAccordionEditFile(event: { file: FolderFile; event: Event }): Promise<void> {
    const { file } = event;
    const { EditFileDialogComponent } = await import('../edit-file-dialog/edit-file-dialog.component');
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;

    const fdate: string = (file as any).fdate ?? '';
    const dateParts = fdate ? fdate.split('/') : [];
    const parsedDate = dateParts.length === 3
      ? new Date(+dateParts[2], parseInt(dateParts[0], 10) - 1, +dateParts[1])
      : null;

    dialogConfig.data = {
      title: 'Edit file',
      label: 'File name',
      name: file.fileName,
      desc: (file as any).fileDesc ?? '',
      date: parsedDate,
      type: 'file'
    };

    const data = await firstValueFrom(
      this.dialog.open(EditFileDialogComponent, dialogConfig).afterClosed()
    );
    if (!data) return;
    await this.applyFileEdit(file, data);
  }

  async handleAccordionCopyFile(event: { file: FolderFile; event: Event }): Promise<void> {
    const { file } = event;
    if (!file.id) {
      this.uiMessaging_$.toastMessage('Cannot copy: missing file document ID.', 'ERROR');
      return;
    }

    const treeData = this.caseFolders
      .map(({ name, color, folderId }: any) => ({ name, id: folderId, color, children: [] }))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));

    const { FolderSelectorComponent } = await import('../ui/folder-selector/folder-selector.component');
    const dialogConfig = new MatDialogConfig();
    dialogConfig.minWidth = '350px';
    dialogConfig.autoFocus = true;
    dialogConfig.data = treeData;

    const folderId: string | undefined = await firstValueFrom(
      this.dialog.open(FolderSelectorComponent, dialogConfig).afterClosed()
    );

    // Empty string = root selected (not allowed); falsy = cancelled
    if (!folderId) return;

    const folder = this.caseFolders.find((f: any) => f.folderId === folderId);
    if (!folder) return;

    try {
      await this.firebase_utilities_$.createFirestoreFilesDuplicates([{ id: file.id }], {
        folderId,
        folderName: folder.name
      });
      this.uiMessaging_$.toastMessage('File copied successfully.', 'INFO');
      this.invalidateFilesLoadedAndRefresh();
    } catch (err: any) {
      this.safeLogger.error('Copy file failed', err?.code ?? 'unknown');
      this.uiMessaging_$.toastMessage('Error copying file. Please try again.', 'ERROR');
    }
  }

  async handleAccordionGenerateDisc(event: { file: FolderFile; event: Event }): Promise<void> {
    const { file } = event;
    const fullFile = this.allFiles.find((f: any) => f.fileId === file.fileId || f.id === file.fileId) ?? file;
    await this.generateDISC(event.event, [fullFile]);
  }

  async generateDISC(evt: Event, selected: any[]): Promise<void> {
    evt.preventDefault();
    if (!Array.isArray(selected) || selected.length === 0) return;

    const { downloadLink, fileDesc, fileId, parentFolderName } = selected[0];
    const uid: string = this.auth_$.userData.getValue()?.['uid'] ?? '';

    this.auth_$.showProgressLoader(
      `Generating disc contents for the study "${fileDesc ?? ''}" in the folder "${parentFolderName ?? ''}"`,
      { position: 'bottom', fileId }
    );
    this.discNotificationIds.push(fileId);

    const generatedDiscExists = async (id: string): Promise<boolean> =>
      !(await this.files_$.getFileByDICOMDIRFileId(id)).empty;

    if (!downloadLink) {
      this.generateDownloadDisc(selected[0], uid);
    } else if (await generatedDiscExists(fileId)) {
      this.auth_$.hideLoader();
      this.auth_$.handleGeneratedDiscCheck({ fileId, fileDesc });
      this.auth_$.hideProgressLoader(fileId);
    } else {
      const fileObj = this.files.find((f: any) => f.fileId === fileId);
      if (fileObj) fileObj.downloadLink = null;
      await this.files_$.deleteDownloadLink(fileId);
      this.generateDownloadDisc(selected[0], uid);
    }
  }

  private generateDownloadDisc(fileSelected: any, uid: string): void {
    this.gapiOperations_$.downloadDICOMFiles(fileSelected, uid).subscribe({
      next: () => {
        this.sessionStorage_$.clearStoppedGeneratedDiscAlert();
      },
      error: (error: any) => {
        this.auth_$.hideProgressLoader(fileSelected.fileId);
        this.safeLogger.error('Disc generation failed', error?.code ?? 'unknown');
        this.uiMessaging_$.toastMessage('Error generating disc. Please try again.', 'ERROR');
      },
      complete: () => { }
    });
  }

  // ── Library / diagnoses top-button actions ────────────────────────────────

  async showLibraryManager(): Promise<void> {
    await this.libraryFacade_$.showLibraryManager(this.getLibraryContext());
  }

  async addDiagnoses(): Promise<void> {
    await this.libraryFacade_$.addDiagnoses(this.getLibraryContext(), this.ownerID);
  }

  async showMyDiagnoses(): Promise<void> {
    const ref = await this.libraryFacade_$.showMyDiagnoses(this.getLibraryContext());
    ref.afterClosed().subscribe({
      next: (answer: any) => {
        const { action, diagnosis } = answer || {};
        if (action === 'edit') {
          this.libraryFacade_$.editDiagnosis(diagnosis, this.getLibraryContext());
        } else if (action === 'delete') {
          this.libraryFacade_$.deleteDiagnosis(diagnosis);
        }
        this.invalidateFilesLoadedAndRefresh();
      },
      error: () => {}
    });
  }

  private getLibraryContext(): LibraryContext {
    return {
      casename: this.casename,
      currentFolder: this.currentFolder,
      currentFolderName: this.currentFolderName,
      caseFolders: this.caseFolders,
      userIsConsultant: this.userIsConsultant,
      userIsClient: this.userIsClient
    };
  }

  // ── Search / add-consultant / add-client-user top-button actions ─────────

  async openFileSearch(): Promise<void> {
    const { SearchFilesComponent } = await import('../search-files/search-files.component');
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '400px';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.maxHeight = '80vw';
    dialogConfig.data = {
      id: 1,
      folder: this.currentFolder,
      clientName: this.patientName,
      casename: this.casename,
      basicfile: this.files,
      action_params: {
        files: '',
        type: '',
        desc: '',
        caseName: this.casename,
        creator: this.auth_$.userData.getValue()?.['uid'] ?? ''
      }
    };

    const dialogRef = this.dialog.open(SearchFilesComponent, dialogConfig);
    const subs = dialogRef.componentInstance.updateResultsEvent.subscribe(() => {
      this.handleSearchResult();
    });
    firstValueFrom(dialogRef.afterClosed()).then(() => subs.unsubscribe());
  }

  handleSearchResult(): void {
    const raw: any[] = this.algolia_$.algoliaSearchResults ?? [];
    const processed = raw.map(item => ({
      ...item,
      parentFolderName:
        item.parentFolderName ||
        this.caseFolders.find((f: any) => f.folderId === item.parentFolder)?.name ||
        ''
    }));
    this.applySearchResults(processed);
  }

  applySearchResults(results: any[]): void {
    this.searchResults = results;
    this.isSearchActive = true;
  }

  clearSearchResults(): void {
    this.searchResults = [];
    this.isSearchActive = false;
  }

  private removeDeletedFileFromSearchResults(fileId: string): void {
    if (!this.isSearchActive || !fileId) return;
    this.searchResults = this.searchResults.filter((f: any) => f.fileId !== fileId);
  }

  async addConsultant(): Promise<void> {
    const plancode = this.auth_$.userData.getValue()?.['plancode'] ?? '';
    const [limits, users] = await Promise.all([
      this.auth_$.getPlanLimits(plancode),
      this.auth_$.getUsersCount()
    ]);
    const { CreateuserComponent } = await import('../createuser/createuser.component');
    const dialogRef = this.dialog.open(CreateuserComponent, {
      width: '680px',
      data: {
        userType: 'Admin',
        title: this.realRole === UserRoles.client ? 'Add an observer' : 'Create a user',
        defaultValue: this.realRole === UserRoles.client ? 'consultant' : null,
        role: this.realRole,
        button: 'Create',
        screen: 'full',
        casename: this.casename,
        limits: { limits: { limits, users } }
      }
    });
    dialogRef.afterClosed().subscribe(async (result: any) => {
      if (result) {
        await this.loadConsultantsForAction();
      }
    });
  }

  async handleAddClientUser(event: any): Promise<void> {
    const { CreateCuaUserComponent } = await import('../create-cua-user/create-cua-user.component');
    const dialogRef = this.dialog.open(CreateCuaUserComponent, {
      width: '600px',
      data: {
        patient: this.patient,
        user: event
      }
    });

    dialogRef.afterClosed().subscribe(async (res: any) => {
      if (!res?.success) return;
      const { email } = res.result ?? {};
      if (email) {
        try {
          await this.clientMatter_$.updateClientMatter(this.casename, email);
          this.patient = { ...this.patient, clientUser: email };
        } catch (err: any) {
          this.safeLogger.error('Update client matter failed', err?.code ?? 'unknown');
        }
      }
      if (!this.userIsConsultant && !this.userIsClient) {
        try {
          await this.folders_$.createFolder(
            'Client Upload',
            this.casename,
            {
              color: folderColors['Client Upload'],
              type: 'folder',
              predefined: true,
              forDelete: false,
              modal: false
            },
            this.caseFolders
          );
        } catch { /* folder may already exist */ }
        await this.loadFolders();
      }
      this.invalidateFilesLoadedAndRefresh();
    });
  }

  // ── Infected files ────────────────────────────────────────────────────────

  private subscribeToInfectedFiles(): void {
    this.infectedFilesSubscription = this.firebase_utilities_$.infectedFiles.subscribe((files: any[]) => {
      this.infectedFiles = files ?? [];
      if (this.infectedFiles.length > 0) {
        this.showInfectedFiles(this.infectedFiles);
      }
    });
  }

  private async showInfectedFiles(files: any[]): Promise<void> {
    const { SimpleMessageWindowComponent } = await import('../ui/simple-message-window/simple-message-window.component');
    this.dialog.open(SimpleMessageWindowComponent, {
      width: '550px',
      disableClose: true,
      data: {
        title: 'Malicious File Detected',
        message: 'The following files are infected and has been deleted:',
        list: files.map((n: any) => ({ name: n })),
        buttons: [{ text: 'CLOSE AND REFRESH', value: 'refresh' }]
      }
    }).afterClosed().subscribe((result: string) => {
      if (result === 'refresh') {
        this.firebase_utilities_$.infectedFiles.next([]);
        this.invalidateFilesLoadedAndRefresh();
      }
    });
  }

  async openCreateFolderDialog(): Promise<void> {
    const { NewFolderComponent } = await import('../new-folder/new-folder.component');
    const result = await firstValueFrom(
      this.dialog.open(NewFolderComponent, {
        data: {
          availableFolders: this.accordionFolders,
          parentFolder: null,
          hideParentFolderSelection: true
        }
      }).afterClosed()
    );
    if (!result?.folderName) return;

    const trimmed: string = result.folderName.trim();
    if (this.caseFolders.some((f: any) => f.name?.toLowerCase() === trimmed.toLowerCase())) {
      this.uiMessaging_$.toastMessage(`Folder "${trimmed}" already exists.`, '');
      return;
    }

    const folderId = this.folders_$.getRandomString(20);
    const newFolder = {
      name: trimmed,
      color: folderColors.custom,
      parentFolder: result.parentFolder?.id || '',
      parentId: result.parentFolder?.id ?? null,
      ownerID: this.userOwnerID,
      type: 'folder',
      belongsTo: this.casename,
      predefined: false,
      forDelete: false,
      folderId,
      children: ''
    };

    try {
      this.auth_$.showLoader('Creating folder...');
      await this.firebase_utilities_$.createFolder(newFolder);
      await this.loadFolders();
      this.uiMessaging_$.toastMessage(`Folder "${trimmed}" created.`, 'INFO');
    } catch (err: any) {
      this.safeLogger.error('Create folder failed', err?.code ?? 'unknown');
    } finally {
      this.auth_$.hideLoader();
    }
  }

  private async applyFileEdit(file: FolderFile, data: any): Promise<void> {
    const updateObject: any = {};
    if (data.name) updateObject['fileName'] = data.name;
    if (data.desc !== undefined) updateObject['fileDesc'] = data.desc;
    if (data.date?.value) {
      const d = new Date(data.date.value);
      updateObject['fdate'] = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    }
    if (!Object.keys(updateObject).length) return;

    try {
      const snap = await this.files_$.getFileByFileId(file.fileId);
      const docId = snap.docs[0]?.id;
      if (!docId) return;
      await this.files_$.updateFileByDocId(docId, updateObject);
      this.uiMessaging_$.toastMessage('File updated successfully.', 'INFORMATION');
      this.invalidateFilesLoadedAndRefresh();
    } catch (err: any) {
      this.safeLogger.error('Edit file failed', err?.code ?? 'unknown');
    }
  }

  private clearFileNotifications(fileId: string): void {
    const uid: string = this.auth_$.userData.getValue()?.['uid'] ?? '';
    if (!uid || !fileId) return;
    this.firebase_utilities_$.deleteNotificationsByTypeUserIdAndFileId(
      [NotificationTypes.newnote, NotificationTypes.modifiednote, NotificationTypes.newfile],
      uid,
      fileId
    );
  }

  private invalidateFilesLoadedAndRefresh(): void {
    this.filesLoaded = {};
    this.handlePostMutationRefresh();
  }

  // ── File-viewer preparation ───────────────────────────────────────────────

  async getFileViewData(ftype: string, fileName: string, viewerurl: string, fileId: string, knownFilePath?: string): Promise<any> {
    return ftype === UploadTypes.DICOMDisk
      ? this.prepareDICOMDiskData(viewerurl)
      : await this.prepareSimpleFileData(fileName, fileId, knownFilePath);
  }

  private prepareDICOMDiskData(viewerurl: string): any {
    return {
      type: 'dicomdisk',
      docviewer: false,
      downloadLink: false,
      viewerUrl: this.getDICOMDiskViewerUrl(viewerurl)
    };
  }

  private getDICOMDiskViewerUrl(viewerurl: string): string {
    return `${environment.config.ohifViewerURL}${viewerurl.substr(viewerurl.indexOf('/projects'))}`;
  }

  private async prepareSimpleFileData(fileName: string, fileId: string, knownFilePath?: string): Promise<any> {
    const ext = this.getFileExtension(fileName);
    if (NOT_PLAYABLE_VIDEO_EXTENSIONS.includes(ext)) {
      return { type: ext, docviewer: false, downloadLink: 'https://www.w3schools.com/html/mov_bbb.mp4' };
    }
    return this.prepareRegularFileViewerData(fileId, fileName, ext, knownFilePath);
  }

  private async prepareRegularFileViewerData(fileId: string, fileName: string, ext: string, knownFilePath?: string): Promise<any> {
    const type = ['png', 'jpg', 'jpeg', 'gif'].includes(ext) ? 'image' : 'regular';
    const filePath = knownFilePath || (await this.files_$.getFileByFileId(fileId)).docs[0].data().filePath;
    const fileUrl = await this.files_$.getFileUrl(filePath);
    return {
      type,
      docviewer: true,
      viewerData: {
        title: fileName,
        src: fileUrl.data.url,
        info: fileUrl.data.url,
        viewer: this.getProperViewer(fileName)
      }
    };
  }

  private getProperViewer(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    switch (ext) {
      case 'mp4': case 'avi': return 'video';
      case 'pdf': return 'pdf';
      case 'doc': case 'docx': case 'xls': case 'xlsx':
      case 'ppt': case 'pptx': case 'csv': return 'office';
      case 'png': case 'jpg': case 'jpeg': case 'gif': return 'url';
      default: return 'google';
    }
  }

  private getFileExtension(fileName: string): string {
    const dot = fileName.lastIndexOf('.');
    return dot === -1 ? '' : fileName.substring(dot + 1).toLowerCase();
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  backToOverview(): void {
    if (!this.isRouteMode && this.dialogRef) {
      this.dialogRef.close();
    }
    if (this.patient) {
      this.auth_$.setPatientProfile(this.patient);
    }
    this.router.navigate(['/patient-overview', this.casename]);
  }

  closeOrNavigateHome(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    this.router.navigate(['/']);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * Load files for the given folder into `this.files`.
   * INDEX_FOLDER_NAME ('all'): loads all case files via getFilesByCaseName.
   * Specific folder: loads direct files + sub-folder items, deduplicates, decorates.
   */
  private async refreshAllFiles(): Promise<void> {
    if (!this.casename) return;
    try {
      const email = this.auth_$.userData.value?.['email'] ?? '';
      let all = (await this.files_$.getFilesByCaseName(this.casename, true, this.userOwnerID))
        .filter((f: any) => f.type !== 'folder');
      if (this.userIsConsultant && email) {
        all = all.filter((f: any) =>
          f.sharedUsers?.some((u: any) => u.email?.toLowerCase() === email.toLowerCase())
        );
      }
      this.allFiles = all;
    } catch { /* allFiles stays as-is if refresh fails */ }
  }

  private async loadFilesForFolder(folderId: string): Promise<void> {
    if (!this.casename) return;
    this.isRefreshing = true;
    this.files = [];
    try {
      const email = this.auth_$.userData.value?.['email'] ?? '';

      if (!folderId || folderId === INDEX_FOLDER_NAME) {
        let files = (await this.files_$.getFilesByCaseName(this.casename, true, this.userOwnerID))
          .filter(f => f.type !== 'folder');
        if (this.userIsConsultant && email) {
          files = files.filter(f =>
            f.sharedUsers?.some((u: any) => u.email?.toLowerCase() === email.toLowerCase())
          );
        }
        files.sort((a, b) => (a.fileName ?? '').toLowerCase() > (b.fileName ?? '').toLowerCase() ? 1 : -1);
        this.files = files;
      } else {
        const [directFiles, directFolders] = await Promise.all([
          this.files_$.getFilesByParentFolderId(
            folderId, this.casename, null, this.userIsConsultant,
            this.userIsConsultant ? email : '', this.ownerID, this.userOwnerID
          ),
          this.files_$.getFolderItemsByParentFolderId(
            folderId, this.casename, null, this.userIsConsultant,
            this.userIsConsultant ? email : '', this.ownerID, this.userOwnerID
          )
        ]);
        const seen = new Map<string, any>();
        [...directFiles, ...directFolders].forEach(item => {
          const key = item.folderId || item.fileId || item.id || Math.random().toString();
          seen.set(key, item);
        });
        const files = Array.from(seen.values()).filter(f => {
          const name = f.filename || f.fileName || f.name;
          return name && name.trim() !== '';
        });
        const decorated = files.map(f => {
          const folder = this.getFolderById(f.parentFolder);
          return { ...f, parentFolderName: folder?.name ?? '', color: folder?.color ?? '' };
        });
        decorated.sort((a, b) => (a.fileName ?? '').toLowerCase() > (b.fileName ?? '').toLowerCase() ? 1 : -1);
        this.files = decorated;
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  private async checkAndCreateDefaultFolders(): Promise<void> {
    if (!this.casename) return;
    const mandatory = await this.getDefaultFolderDefs();
    const existing = (await this.firebase_utilities_$.getPatientDefaultFolders(this.casename, this.ownerID)) || [];
    const missing = mandatory.filter(f => !existing.some((e: any) => e.name === f.name));
    if (!missing.length) return;

    await Promise.all(
      missing.map(({ name, color }) =>
        this.firebase_utilities_$.createFolder({
          children: '',
          name,
          color,
          parentFolder: '',
          type: 'folder',
          belongsTo: this.casename,
          predefined: true,
          forDelete: false,
          folderId: this.folders_$.getRandomString(20),
          ownerID: this.ownerID
        })
      )
    );

    await this.firebase_utilities_$.updateDefaultFolders(true, {
      casename: this.casename,
      ...(this.ownerID && { ownerID: this.ownerID })
    });
  }

  private async getDefaultFolderDefs(): Promise<{ name: string; color: string }[]> {
    try {
      const snap = await firebase.firestore().collection('defaultFolders').get();
      return snap.docs
        .map(d => d.data() as { name: string; color: string; forbiddenPatientType?: string })
        .filter(d => d.forbiddenPatientType !== this.patient?.type);
    } catch {
      return [
        { name: LIBRARY_FOLDER_NAME, color: folderColors.custom },
        { name: DISCS_FOLDER_NAME, color: folderColors.custom }
      ];
    }
  }

  private extractPatientFields(patient: any): void {
    this.casename = patient.caseName || patient.LegalCaseId || patient.legalCaseId || patient.patientDocId || '';
    this.patientName = this.getPatientName(patient);
    this.defaultFolders = Boolean(patient.defaultFolders);
  }

  private getPatientName(patient: any): string {
    if (!patient) return '';
    const first = patient.firstname || patient.firstName || patient.first_name || '';
    const last = patient.lastname || patient.lastName || patient.last_name || '';
    if (!first && !last) {
      return patient.name || patient.fullName || patient.patientName || '';
    }
    const fmt = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
    return `${fmt(first)} ${fmt(last)}`.trim();
  }

  private waitForUserData(): Promise<void> {
    const current = this.auth_$.userData.value;
    if (current && Object.keys(current).length > 0 && current['ownerID']) {
      this.userOwnerID = current['ownerID'];
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      const sub = this.auth_$.userData.subscribe(data => {
        if (data && Object.keys(data).length > 0 && data['ownerID']) {
          this.userOwnerID = data['ownerID'];
          sub.unsubscribe();
          resolve();
        }
      });
      setTimeout(() => {
        sub.unsubscribe();
        this.userOwnerID = this.ownerID;
        resolve();
      }, 5000);
    });
  }
}
